import { type FC, useCallback, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form-latest';

import { _isNotEmpty, _notNil } from '@/littledash';

import type { DisplayedGroupMetadata, MetadataField } from '@/model/Metadata.model';
import type { ID } from '@/model/Common.model';
import InVivoError from '@/model/InVivoError';

import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import { DragHintBanner } from '@/components/UI/Banner/Reusable';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import Header from '@/components/UI/Header';
import DraggableList, { asSortableListItem } from '@/components/UI/List/DraggableList';
import SearchSelect from '@/components/UI/SearchSelect';
import { errorToast, successToast } from '@/helpers';

type GroupMetadataValue = Omit<MetadataField, 'required'> & { required?: boolean };

interface GroupFormData {
  metadata: Array<{ value: GroupMetadataValue }>;
}

interface StudyGroupsProps {
  groupMetadata: Array<MetadataField>;
  savedGroupData: Array<DisplayedGroupMetadata>;
}

interface MetadataFormItemProps {
  id: string;
  value: GroupMetadataValue;
  index: number;
}

const StudyGroups: FC<StudyGroupsProps> = ({ groupMetadata, savedGroupData }) => {
  const defaultSection = {
    metadata: groupMetadata ?? [],
  };

  const getSelectedIds = useCallback((): Array<ID> => {
    const savedIds = savedGroupData.map((s) => s.schema.id);
    return defaultSection.metadata.reduce<Array<ID>>((acc, s) => {
      if (savedIds.includes(s.id)) {
        acc.push(s.id);
      }
      return acc;
    }, []);
  }, [savedGroupData, defaultSection.metadata]);

  const metaIsRequired = useCallback(
    (id: ID): boolean => {
      if (_notNil(id)) {
        return savedGroupData.find((meta) => meta.id === id)?.required ?? false;
      }
      return false;
    },
    [savedGroupData]
  );

  const [selected, setSelected] = useState<Array<ID>>(() => getSelectedIds());
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);

  const formMethods = useForm<GroupFormData>({
    defaultValues: {
      metadata: savedGroupData.reduce<Array<{ value: GroupMetadataValue }>>((acc, data) => {
        if (selected.includes(data.id)) {
          acc.push({
            value: { ...data.schema, required: metaIsRequired(data.id) },
          });
        }
        return acc;
      }, []),
    },
  });

  const { control, handleSubmit } = formMethods;

  const { fields, move, append, remove } = useFieldArray({
    control,
    name: 'metadata',
  });

  const handleReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      move(sourceIndex, destinationIndex);
    },
    [move]
  );

  const onSubmit = useCallback(
    async (data: GroupFormData) => {
      setSubmitting(true);
      const metadata = data.metadata?.map((meta) => meta.value) ?? [];
      try {
        await Http.put(apiRoute('team.studies.groups'), {
          metadata,
        });
        successToast('Group metadata saved.');
      } catch (error) {
        setApiError(error);
        ExceptionHandler.captureException(
          new InVivoError('Failed to save group metadata', {
            cause: error,
            slug: 'study-groups-save',
          })
        );
        errorToast('There was a problem with your submission.');
      } finally {
        setSubmitting(false);
      }
    },
    [setSubmitting, setApiError]
  );

  const handleSelect = useCallback(
    ({ type, value }: { type: 'ADDED' | 'REMOVED'; value: ID }) => {
      if (type === 'ADDED') {
        const metadata = groupMetadata.find((meta) => Number(meta.id) === Number(value));
        if (_notNil(metadata)) {
          append({
            value: {
              ...metadata,
            },
          });
        }
      } else {
        const index = fields.findIndex((field) => Number(field.value.id) === Number(value));
        if (index !== -1) {
          remove(index);
        }
      }
    },
    [groupMetadata, fields, append, remove]
  );

  const handleSelectAll = useCallback(() => {
    remove(fields.map((_: unknown, i: number) => i));
    append(groupMetadata.map((meta) => ({ value: { ...meta, required: false } })));
  }, [fields, remove, append, groupMetadata]);

  const handleClearSelection = useCallback(() => {
    remove(fields.map((_: unknown, i: number) => i));
  }, [fields, remove]);

  return (
    <FormProvider {...formMethods}>
      {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="mb3">
            <Header mainHeaderText="Groups" />
            <h3 className="f6 normal lh-title pb2 dark-gray mb3">
              Define what group metadata fields show during study creation and what is required by default.{' '}
            </h3>
          </div>
          <div className="w-50">
            {_isNotEmpty(fields) && fields.length > 1 && (
              <div className="pb3">
                <DragHintBanner />
              </div>
            )}
            <div className="flex w-100">
              <div className="w-50">
                <SearchSelect
                  className="ui-card"
                  sections={[
                    {
                      items: groupMetadata,
                    },
                  ]}
                  selected={selected}
                  setSelected={setSelected}
                  onSelect={handleSelect}
                  onSelectAll={handleSelectAll}
                  onClearSelection={handleClearSelection}
                />
              </div>
              <div className="w-50 pl3">
                <div className="ui-card pa3 overflow-auto" style={{ height: 409 }}>
                  {_isNotEmpty(fields) ? (
                    <DraggableList
                      canAmend={false}
                      data={fields}
                      sortable={{
                        droppableId: 'study-group-metadata-item',
                        onReorder: handleReorder,
                        disabled: fields.length <= 1,
                      }}
                      renderItem={asSortableListItem({
                        canSort: fields.length > 1,
                        itemClassName: 'bb b--moon-gray pb3 mb3',
                      })(MetadataFormItem)}
                    />
                  ) : (
                    <div className="bg-light-silver pa3 br2 w-100 h-100 items-center flex">
                      <h3 className="lh-title f5 w-100 tc">Select metadata for this section</h3>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="pv3">
              <Button submit disabled={submitting}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const MetadataFormItem: FC<MetadataFormItemProps> = ({ value, index }) => {
  const { control } = useFormContext<GroupFormData>();

  return (
    <Controller
      key={index}
      defaultValue={value}
      name={`metadata.${index}.value`}
      control={control}
      render={({
        field: { onChange, value: fieldValue },
      }: {
        field: { onChange: (v: GroupMetadataValue) => void; value: GroupMetadataValue };
      }) => (
        <div>
          <h3 className="f6 normal lh-title pb2">{fieldValue.title}</h3>
          <Checkbox
            className="pointer"
            label="Required"
            name={String(fieldValue.id)}
            checked={fieldValue.required === true}
            onChange={() =>
              onChange({
                ...fieldValue,
                required: !fieldValue.required,
              })
            }
          />
        </div>
      )}
    />
  );
};

export default StudyGroups;
